import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FactoryHeader from "./components/FactoryHeader";
import FactoryProducts from "./components/FactoryProducts";
import FactoryReviews from "./components/FactoryReviews";
import FactoryCertifications from "./components/FactoryCertifications";
import QuoteRequestModal from "./components/QuoteRequestModal";
import { mockFactories } from "@/mocks/factories";
import SEO from "@/components/base/SEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

type Tab = "products" | "certifications" | "about" | "reviews";

const tabs: { value: Tab; label: string; icon: string }[] = [
  { value: "products", label: "Products", icon: "ri-store-2-line" },
  { value: "certifications", label: "Certifications", icon: "ri-award-line" },
  { value: "about", label: "About & Details", icon: "ri-building-2-line" },
  { value: "reviews", label: "Reviews", icon: "ri-star-line" },
];

export default function FactoryPage() {
  const { id } = useParams<{ id: string }>();
  const factory = mockFactories.find((f) => f.id === id) || mockFactories[0];
  const [activeTab, setActiveTab] = useState<Tab>("products");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...Array.from(new Set(factory.products.map((p) => (p as { category?: string }).category || "Other")))];

  const filteredProducts = activeCategory === "All"
    ? factory.products
    : factory.products.filter((p) => (p as { category?: string }).category === activeCategory);

  const factorySchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": factory.name,
    "description": `Verified USA factory — ${factory.name}. Browse full product catalog, certifications, and request a quote directly.`,
    "url": `${SITE_URL}/factory/${factory.id}`,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": factory.rating,
      "reviewCount": factory.totalReviews,
    },
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f5f5" }}>
      <SEO
        title={`${factory.name} — Verified Factory Profile & Catalog | StarBookPrime`}
        description={`Browse ${factory.name}'s full product catalog, certifications, and factory details. Request a quote directly. Verified USA factory on StarBookPrime.`}
        keywords={`${factory.name}, verified factory catalog, factory certifications, request quote`}
        canonical={`/factory/${factory.id}`}
        schema={factorySchema}
      />
      <Navbar />

      {showQuoteModal && (
        <QuoteRequestModal factoryName={factory.name} onClose={() => setShowQuoteModal(false)} />
      )}

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <Link to="/" className="hover:text-orange-500 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/marketplace" className="hover:text-orange-500 transition-colors cursor-pointer">Marketplace</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-600">{factory.name}</span>
          </nav>

          {/* Reseller CTA Banner */}
          <div className="bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white">
              <p className="font-bold text-base flex items-center gap-2">
                <i className="ri-star-fill" /> Resellers &amp; Stars — Earn Commission from This Factory!
              </p>
              <p className="text-white/80 text-sm mt-0.5">
                Each product shows the commission % set by {factory.name}. Copy your referral link and start earning via PayPal.
              </p>
            </div>
            <Link
              to="/influencer"
              className="flex-shrink-0 px-5 py-2.5 bg-white text-rose-600 font-bold text-sm rounded-xl hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-star-line mr-1" /> Join as Reseller
            </Link>
          </div>

          {/* Factory Header */}
          <FactoryHeader factory={factory} onRequestQuote={() => setShowQuoteModal(true)} />

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 bg-white rounded-xl border border-gray-100 p-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${activeTab === tab.value ? "bg-orange-500 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >
                <i className={tab.icon} />
                {tab.label}
                {tab.value === "products" && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.value ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                    {factory.products.length}
                  </span>
                )}
                {tab.value === "certifications" && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.value ? "bg-white/20 text-white" : "bg-green-100 text-green-600"}`}>
                    {(factory as { certifications?: unknown[] }).certifications?.length ?? 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab: Products */}
          {activeTab === "products" && (
            <div>
              {/* Category Filter */}
              {categories.length > 2 && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeCategory === cat ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <FactoryProducts products={filteredProducts} factoryName={factory.name} />
              </div>

              {/* Quote CTA */}
              <div className="mt-6 bg-gray-900 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-white">
                  <h3 className="text-xl font-extrabold mb-1">Need a Custom Order or Bulk Quote?</h3>
                  <p className="text-gray-400 text-sm">Tell {factory.name} your requirements — quantity, specs, branding — and get a competitive quote sent to your email.</p>
                </div>
                <button
                  onClick={() => setShowQuoteModal(true)}
                  className="flex-shrink-0 flex items-center gap-2 px-7 py-3.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line" /> Request a Quote
                </button>
              </div>
            </div>
          )}

          {/* Tab: Certifications */}
          {activeTab === "certifications" && (
            <FactoryCertifications
              certifications={(factory as { certifications?: { name: string; issuer: string; year: string; icon: string; color: string }[] }).certifications ?? []}
              factoryName={factory.name}
              founded={(factory as { founded?: string }).founded ?? "N/A"}
              employees={(factory as { employees?: string }).employees ?? "N/A"}
              annualRevenue={(factory as { annualRevenue?: string }).annualRevenue ?? "N/A"}
              moq={(factory as { moq?: string }).moq ?? "N/A"}
              leadTime={(factory as { leadTime?: string }).leadTime ?? "N/A"}
              exportCountries={(factory as { exportCountries?: string[] }).exportCountries ?? []}
              about={factory.about}
            />
          )}

          {/* Tab: About */}
          {activeTab === "about" && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-building-2-line text-orange-500" /> About {factory.name}
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{factory.about}</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    { icon: "ri-calendar-line", label: "Founded", value: (factory as { founded?: string }).founded ?? "N/A" },
                    { icon: "ri-team-line", label: "Employees", value: (factory as { employees?: string }).employees ?? "N/A" },
                    { icon: "ri-money-dollar-circle-line", label: "Annual Revenue", value: (factory as { annualRevenue?: string }).annualRevenue ?? "N/A" },
                    { icon: "ri-stack-line", label: "Min. Order", value: (factory as { moq?: string }).moq ?? "N/A" },
                    { icon: "ri-time-line", label: "Lead Time", value: (factory as { leadTime?: string }).leadTime ?? "N/A" },
                    { icon: "ri-map-pin-line", label: "Location", value: factory.location },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-50 rounded-xl p-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg mb-2">
                        <i className={`${item.icon} text-orange-500 text-sm`} />
                      </div>
                      <p className="text-sm font-bold text-gray-900">{item.value}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warehouses */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-building-4-line text-orange-500" /> Warehouse Locations
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {factory.warehouses.map((wh) => (
                    <div key={wh.type} className={`flex items-center gap-4 p-5 rounded-xl border ${wh.type === "local" ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"}`}>
                      <span className="text-3xl">{wh.flag}</span>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{wh.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{wh.location}</p>
                        <p className={`text-xs font-semibold mt-1 ${wh.type === "local" ? "text-green-600" : "text-orange-600"}`}>
                          <i className="ri-truck-line mr-1" />{wh.shippingTime}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Countries */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-global-line text-orange-500" /> Export Markets
                </h2>
                <div className="flex flex-wrap gap-2">
                  {((factory as { exportCountries?: string[] }).exportCountries ?? []).map((country) => (
                    <span key={country} className="px-3 py-1.5 bg-orange-50 border border-orange-100 text-orange-700 text-sm font-medium rounded-full">
                      {country}
                    </span>
                  ))}
                </div>
              </div>

              {/* Quote CTA */}
              <div className="bg-gray-900 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-white">
                  <p className="font-bold text-base">Ready to work with {factory.name}?</p>
                  <p className="text-gray-400 text-sm mt-0.5">Submit your requirements and get a quote sent to your email.</p>
                </div>
                <button
                  onClick={() => setShowQuoteModal(true)}
                  className="flex-shrink-0 px-6 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-file-list-3-line mr-2" />Request a Quote
                </button>
              </div>
            </div>
          )}

          {/* Tab: Reviews */}
          {activeTab === "reviews" && (
            <FactoryReviews reviews={factory.reviews} rating={factory.rating} totalReviews={factory.totalReviews} />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
